/**
 * Phase 7+ — shared Festivals editor.
 *
 * Originally lived inline inside `OvcBlock.js` (per-buffet-meal). Phase
 * 7++ extends festivals to regular + specialty restaurants too, so we
 * extract the editor to a single component driven entirely by props.
 *
 * Each festival card has:
 *   - Library picker (drop-down of `festivalsLib`, plus an "unlinked /
 *     custom" sentinel and "Other..." entry)
 *   - Free-text name input (kept in sync when picking a lib entry)
 *   - Start / End HH:MM inputs
 *   - Dishes textarea (one per line)
 *
 * The `tidPrefix` prop drives every `data-testid` so the same component
 * can be mounted in multiple places on a page without collisions
 * (e.g. `regular-r123` vs `specialty-r456` vs `buffet-OVC-lunch`).
 */
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { arrToText, textToArr } from "@/components/mealplan/textListHelpers";

const NONE = "__none__";
const OTHER = "__other__";

export default function FestivalsEditor({
  value, onChange, festivalsLib, tidPrefix,
}) {
  const list = Array.isArray(value) ? value : [];
  const update = (idx, patch) =>
    onChange(list.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const remove = (idx) =>
    onChange(list.filter((_, i) => i !== idx));
  const add = () =>
    onChange([
      ...list,
      { festival_id: null, name: "", start: "", end: "", dishes: [] },
    ]);
  const pickFromLib = (idx, libId) => {
    if (libId === NONE) {
      update(idx, { festival_id: null });
      return;
    }
    if (libId === OTHER) {
      update(idx, { festival_id: null, name: "" });
      return;
    }
    const lib = (festivalsLib || []).find((f) => f.id === libId);
    if (!lib) return;
    const cur = list[idx] || {};
    const patch = { festival_id: libId, name: lib.name };
    if (!(cur.start || "").trim()) patch.start = lib.default_start || "";
    if (!(cur.end || "").trim()) patch.end = lib.default_end || "";
    if (!(cur.dishes || []).length) patch.dishes = [...(lib.dishes || [])];
    update(idx, patch);
  };

  return (
    <div
      className="mt-2 rounded-md border border-rose-200 bg-rose-50/60 px-2.5 py-2"
      data-testid={`${tidPrefix}-festivals`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wider text-rose-800">
          Festivals {list.length > 0 && `(${list.length})`}
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={add}
          className="h-6 px-2 text-[10px] text-rose-800 hover:bg-rose-100"
          data-testid={`${tidPrefix}-festival-add`}
        >
          <Plus className="h-3 w-3" />
          Add festival
        </Button>
      </div>
      {list.length === 0 ? (
        <p className="mt-1 text-[10px] italic text-rose-700/70">
          No festivals scheduled. Click "Add festival" for a timed event.
        </p>
      ) : (
        <div className="mt-1.5 space-y-2">
          {list.map((f, idx) => (
            <div
              key={idx}
              className="rounded-md border border-rose-300 bg-white p-2"
              data-testid={`${tidPrefix}-festival-${idx}`}
            >
              <div className="flex items-start gap-1.5">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Select
                    value={f.festival_id || ""}
                    onValueChange={(id) => pickFromLib(idx, id)}
                  >
                    <SelectTrigger
                      className="h-7 flex-1 text-xs"
                      data-testid={`${tidPrefix}-festival-${idx}-select`}
                    >
                      <SelectValue placeholder="Pick from library" />
                    </SelectTrigger>
                    <SelectContent>
                      {(festivalsLib || []).map((lf) => (
                        <SelectItem key={lf.id} value={lf.id}>{lf.name}</SelectItem>
                      ))}
                      <SelectItem value={NONE}>(unlinked / custom)</SelectItem>
                      <SelectItem value={OTHER}>Other…</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={f.name || ""}
                    onChange={(e) =>
                      update(idx, { name: e.target.value, festival_id: null })
                    }
                    placeholder="Festival name"
                    className="h-7 text-xs"
                    data-testid={`${tidPrefix}-festival-${idx}-name`}
                  />
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="time"
                      value={f.start || ""}
                      onChange={(e) => update(idx, { start: e.target.value })}
                      className="h-7 w-24 text-xs"
                      data-testid={`${tidPrefix}-festival-${idx}-start`}
                    />
                    <span className="text-[10px] text-muted-foreground">–</span>
                    <Input
                      type="time"
                      value={f.end || ""}
                      onChange={(e) => update(idx, { end: e.target.value })}
                      className="h-7 w-24 text-xs"
                      data-testid={`${tidPrefix}-festival-${idx}-end`}
                    />
                  </div>
                  <Textarea
                    value={arrToText(f.dishes || [])}
                    onChange={(e) =>
                      update(idx, { dishes: textToArr(e.target.value) })
                    }
                    placeholder="Dishes — one per line"
                    className="min-h-[50px] text-xs"
                    data-testid={`${tidPrefix}-festival-${idx}-dishes`}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(idx)}
                  className="h-7 w-7 shrink-0 text-destructive hover:bg-destructive/10"
                  data-testid={`${tidPrefix}-festival-${idx}-remove`}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
